import React, {useState, useEffect, useRef} from 'react';
import {Modal, Table, Button, Checkbox, DatePicker, Select, message, Tag, Input} from 'antd';
import {useQuery} from '@apollo/client';
import {GET_PICKUP_APPOINTMENT_LISTING} from '../graphql/queries/getPickupAppointmentListing';
import type {
    PickupAppointmentType,
    PickupAppointmentListingResponse
} from '../graphql/queries/getPickupAppointmentListing';
import type {ColumnsType} from 'antd/es/table';
import dayjs from 'dayjs';
import {CloseOutlined, LeftOutlined, RightOutlined} from '@ant-design/icons';
import {packageUnitFilterOptions, statusOptions} from '../types/PickupAppointmentType';
import { useWarehouseContext } from "../../../../contexts/WarehouseContext";
import { useTranslation } from 'react-i18next';
import TablePaginationBar from '../../../../components/TablePaginationBar';

interface AddAppointmentModalProps {
    visible: boolean;
    onClose: () => void;
    onAdd: (selectedAppointments: PickupAppointmentType[]) => void;
    disabledIds?: number[]; // 已选择的预约ID列表，这些ID在弹框中不可选
    pickupDate?: string; // 格式：YYYY-MM-DD，如果不传则使用今天
}

const PickupAppointmentAddModal: React.FC<AddAppointmentModalProps> = ({
                                                                           visible,
                                                                           onClose,
                                                                           onAdd,
                                                                           disabledIds = [],
                                                                           pickupDate
                                                                       }) => {
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [selectedRows, setSelectedRows] = useState<PickupAppointmentType[]>([]);
    // 用于保存跨页面的所有选中数据
    const [allSelectedRows, setAllSelectedRows] = useState<PickupAppointmentType[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [zipcode, setZipcode] = useState<string | undefined>(undefined);
    const [unitType, setUnitType] = useState<string | undefined>(undefined);
    const [appointmentNumber, setAppointmentNumber] = useState<string>('');
    const [appointmentStatus, setAppointmentStatus] = useState<string | undefined>('CREATED');
    const [pickupSiteId, setPickupSiteId] = useState<string>('');
    const [addressFilter, setAddressFilter] = useState<string>('');
    const [contactPhoneFilter, setContactPhoneFilter] = useState<string>('');
    const [isValidating, setIsValidating] = useState(false);
    const firstLoadRef = useRef(true);
    const { currentWarehouse } = useWarehouseContext();
    const warehouseId = currentWarehouse?.id;
    const {t} = useTranslation();

    // 使用传入的 pickupDate 或当前日期，不可更改
    const currentPickupDate = pickupDate || dayjs().format('YYYY-MM-DD');
    const currentPickupDateDayjs = dayjs(currentPickupDate); // 用于DatePicker

    // 支持多个预约单号，使用多种分隔符：逗号、空格、换行、分号、制表符
    const appointmentNumberTrimmed = appointmentNumber.trim();
    const appointmentNumberFilters = appointmentNumberTrimmed
        ? appointmentNumberTrimmed
            .split(/[,\s\n\r\t;]+/)
            .map(s => s.trim())
            .filter(s => s.length > 0)
        : undefined;
    const pickupSiteIdTrimmed = pickupSiteId.trim();
    const pickupSiteIdNumber = pickupSiteIdTrimmed !== '' ? Number(pickupSiteIdTrimmed) : undefined;
    const cooperationWarehouseIdFilter = pickupSiteIdNumber !== undefined && !Number.isNaN(pickupSiteIdNumber)
        ? pickupSiteIdNumber
        : undefined;
    const addressTrimmed = addressFilter.trim();
    const contactPhoneTrimmed = contactPhoneFilter.trim();

    // 查询参数
    const variables = {
        filters: {
            expectedPickupDate: currentPickupDate,
            status: appointmentStatus,
            zipcode: zipcode,
            expectedParcelUnit: unitType,
            warehouseId: warehouseId ? Number(warehouseId) : undefined,
            appointmentNumberList: appointmentNumberFilters,
            cooperationWarehouseId: cooperationWarehouseIdFilter,
            address: addressTrimmed || undefined,
            contactPhone: contactPhoneTrimmed || undefined
        },
        pageRequest: {
            page: currentPage,
            size: pageSize,
            sortBy: "pickupDate",
            sortDirection: "DESC"
        }
    };

    // 执行查询
    const {data, loading, refetch} = useQuery<PickupAppointmentListingResponse>(
        GET_PICKUP_APPOINTMENT_LISTING,
        {
            variables,
            fetchPolicy: 'network-only',
            skip: !visible || !warehouseId // 只有在模态框可见且有 warehouseId 时才执行查询
        }
    );

    // 当模态框打开时重新获取数据
    useEffect(() => {
        if (visible) {
            // 重新获取数据
            refetch(variables);

            // 重置筛选条件
            if (!firstLoadRef.current) {
                setZipcode(undefined);
                setUnitType(undefined);
                setAppointmentNumber('');
                setAppointmentStatus('CREATED');
                setPickupSiteId('');
                setAddressFilter('');
                setContactPhoneFilter('');
                setCurrentPage(1);
            }
            firstLoadRef.current = false;
        }
    }, [visible]);

    // 当筛选条件变化时重新查询
    useEffect(() => {
        if (visible && !firstLoadRef.current) {
            refetch(variables);
        }
    }, [currentPage, pageSize, zipcode, unitType, appointmentNumber, appointmentStatus, pickupSiteId, addressFilter, contactPhoneFilter]);

    // 重置选择
    useEffect(() => {
        if (!visible) {
            setSelectedRowKeys([]);
            setSelectedRows([]);
            setAllSelectedRows([]);
        }
    }, [visible]);

    // 当数据加载完成后，更新当前页面的选中状态
    useEffect(() => {
        if (data?.getPickupAppointmentListing?.content) {
            const currentPageData = data.getPickupAppointmentListing.content;
            // 从所有选中数据中找出当前页面应该选中的项
            const currentPageSelectedKeys: React.Key[] = [];
            const currentPageSelectedRows: PickupAppointmentType[] = [];
            
            currentPageData.forEach(item => {
                const isSelected = allSelectedRows.some(selected => selected.id === item.id);
                if (isSelected) {
                    currentPageSelectedKeys.push(item.id);
                    currentPageSelectedRows.push(item);
                }
            });
            
            setSelectedRowKeys(currentPageSelectedKeys);
            setSelectedRows(currentPageSelectedRows);
        }
    }, [data, allSelectedRows]);

    // 表格列定义
    const columns: ColumnsType<PickupAppointmentType> = [
        {
            title: t('pickup.appointment.taskCreateDrawer.appointmentNumber') || '',
            dataIndex: 'appointmentNumber',
            key: 'appointmentNumber',
            width: 180,
            render: (text) => <span className="text-purple-600">{text}</span>
        },
        {
            title: t('pickup.appointment.taskCreateDrawer.customerName') || '',
            dataIndex: 'channelName',
            key: 'channelName',
            width: 150,
        },
        {
            title: t('pickup.appointment.taskCreateDrawer.pickupSite') || '',
            dataIndex: 'cooperationWarehouseId',
            key: 'cooperationWarehouseId',
            width: 150,
        },
        {
            title: t('pickup.appointment.taskCreateDrawer.phoneNumber') || '',
            dataIndex: 'contactPhone',
            key: 'contactPhone',
            width: 150,
        },
        {
            title: t('pickup.appointment.taskCreateDrawer.address') || '',
            key: 'address',
            width: 200,
            render: (_, record) => (
                <div className="whitespace-pre-line">
                    {record.addressLine1}
                    {record.addressLine2 && <div>{record.addressLine2}</div>}
                </div>
            ),
        },
        {
            title: t('pickup.appointment.taskCreateDrawer.zipcode') || '',
            dataIndex: 'zipcode',
            key: 'zipcode',
            width: 100,
        },
        {
            title: t('pickup.appointment.taskCreateDrawer.expectedTime') || '',
            key: 'expectedPickupTime',
            width: 150,
            render: (_, record) => (
                <div>{record.expectedPickupTimeStart}-{record.expectedPickupTimeEnd}</div>
            ),
        },
        {
            title: t('pickup.appointment.taskCreateDrawer.timezone') || '',
            dataIndex: 'timezone',
            key: 'timezone',
            width: 100,
        },
        {
            title: t('pickup.appointment.taskCreateDrawer.expectedCount') || '',
            dataIndex: 'expectedParcelCount',
            key: 'expectedParcelCount',
            width: 150,
            align: 'left',
        },
        {
            title: t('pickup.appointment.taskCreateDrawer.expectedUnit') || '',
            dataIndex: 'expectedParcelUnit',
            key: 'expectedParcelUnit',
            width: 150,
            align: 'left',
            render: (unit) => packageUnitFilterOptions.find(option => option.value === unit)?.label || unit || '--',
        },
        {
            title: t('pickup.appointment.taskCreateDrawer.status') || '',
            dataIndex: 'status',
            key: 'status',
            width: 120,
            render: (status, record) => {
                const reason = getDisabledReason(record);
                if (reason) {
                    return (
                        <div>
                            <Tag color={status === 'CREATED' ? 'orange' : 'red'}>{status}</Tag>
                            {reason !== `Status: ${status}` && (
                                <div className="text-xs text-gray-500 mt-1">{reason}</div>
                            )}
                        </div>
                    );
                }
                return <Tag color="green">{status}</Tag>;
            }
        },
    ];

    // 处理表格选择变化
    const handleSelectChange = (newSelectedRowKeys: React.Key[], newSelectedRows: PickupAppointmentType[]) => {
        setSelectedRowKeys(newSelectedRowKeys);
        setSelectedRows(newSelectedRows);
        
        // 更新跨页面的所有选中数据
        const currentPageData = data?.getPickupAppointmentListing?.content || [];
        
        // 从所有选中数据中移除当前页面的数据
        const otherPagesSelected = allSelectedRows.filter(item => 
            !currentPageData.some(pageItem => pageItem.id === item.id)
        );
        
        // 合并其他页面的选中数据和当前页面的新选中数据
        const newAllSelectedRows = [...otherPagesSelected, ...newSelectedRows];
        setAllSelectedRows(newAllSelectedRows);
    };

    // 原「合作仓库状态校验」接口服务端已是空实现、恒返回通过，已移除该调用（P2 #1）
    const validateSelectedAppointments = async (_appointmentIds: number[]): Promise<boolean> => {
        return true;
    };

    // 处理添加预约
    const handleAdd = async () => {
        if (allSelectedRows.length === 0) {
            message.warning(t('pickup.appointment.taskCreateDrawer.messages.pleaseSelectAtLeastOneAppointment') || '');
            return;
        }

        // 开始验证
        setIsValidating(true);
        
        try {
            // 验证选中预约单的合作仓库状态
            const appointmentIds = allSelectedRows.map(row => row.id);
            const isValid = await validateSelectedAppointments(appointmentIds);
            
            if (!isValid) {
                // 验证失败，不关闭模态框，保持在当前页面
                return;
            }

            // 验证成功，继续添加操作
            onAdd(allSelectedRows);
            onClose();
        } finally {
            setIsValidating(false);
        }
    };

    // 处理分页变化
    const handlePageChange = (page: number, size?: number) => {
        setCurrentPage(page);
        if (size) setPageSize(size);
    };

    // 添加All选项
    const unitOptions = [
        {label: 'All', value: undefined},
        ...packageUnitFilterOptions
    ];

    // 获取总记录数
    const totalElements = data?.getPickupAppointmentListing?.totalElements || 0;

    // 获取禁用原因
    const getDisabledReason = (record: PickupAppointmentType): string => {
        if (disabledIds.includes(record.id)) {
            return 'Already selected';
        }
        if (record.status !== 'CREATED') {
            return `Status: ${record.status}`;
        }
        if (record.taskNumber) {
            return `Already in task: ${record.taskNumber}`;
        }
        return '';
    };

    return (
        <Modal
            title={
                <div className="flex items-center">
                    <div className="h-6 w-1 bg-purple-600 mr-3"></div>  
                    <span className="text-xl font-medium">{t('pickup.appointment.taskCreateDrawer.addAppointment') || ''}</span>
                </div>
            }
            open={visible}
            onCancel={onClose}
            width={1600}
            footer={null}
            closeIcon={<CloseOutlined/>}
            destroyOnClose={true}
        >
            <div className="space-y-4">
                {/* 筛选条件 */}
                <div className="flex items-center space-x-4 mb-4">
                    <div className="flex items-center">
                        <span className="mr-2">{t('pickup.appointment.taskCreateDrawer.pickupDate') || ''}</span>
                        <DatePicker
                            value={currentPickupDateDayjs}
                            disabled
                            style={{width: 120}}
                        />
                    </div>
                    <div className="flex items-center">
                        <span className="mr-2">{t('pickup.appointment.taskCreateDrawer.appointmentNumber') || ''}</span>
                        <Input
                            style={{width: 180}}
                            value={appointmentNumber}
                            onChange={(event) => setAppointmentNumber(event.target.value)}
                            placeholder={t('pickup.appointment.taskCreateDrawer.appointmentNumber') || '支持多个，用逗号或空格分隔'}
                        />
                    </div>
                    <div className="flex items-center">
                        <span className="mr-2">
                            {(t('pickup.appointment.taskCreateDrawer.pickupSite') || 'Pickup Site')}
                        </span>
                        <Input
                            style={{width: 100}}
                            value={pickupSiteId}
                            onChange={(event) => setPickupSiteId(event.target.value)}
                            placeholder={(t('pickup.appointment.taskCreateDrawer.pickupSite') || 'Pickup Site')}
                        />
                    </div>
                    <div className="flex items-center">
                        <span className="mr-2">{t('pickup.appointment.taskCreateDrawer.status') || ''}</span>
                        <Select
                            style={{width: 100}}
                            options={statusOptions}
                            value={appointmentStatus}
                            onChange={(value) => setAppointmentStatus(value)}
                            placeholder={t('components.input.all') || ''}
                            allowClear
                        />
                    </div>
                    <div className="flex items-center space-x-4 ml-auto">
                        {allSelectedRows.length > 0 && (
                            <div className="text-purple-600 text-sm">
                                <span className="bg-purple-50 px-2 py-1 rounded">
                                    {allSelectedRows.length} {t('pickup.appointment.taskCreateDrawer.appointment') || ''} {t('components.input.selected') || ''}
                                </span>
                            </div>
                        )}
                        {disabledIds.length > 0 && (
                            <div className="text-gray-500 text-sm">
                                <span className="bg-gray-50 px-2 py-1 rounded">
                                    {disabledIds.length} {t('pickup.appointment.taskCreateDrawer.appointment') || ''} {t('components.input.unselected') || ''}
                                </span>
                            </div>
                        )}
                    </div>
                </div>

                <div className="flex items-center space-x-4 mb-4">
                    <div className="flex items-center">
                        <span className="mr-2">{t('pickup.appointment.taskCreateDrawer.zipcode') || ''}</span>
                        <Select
                            style={{width: 120}}
                            value={zipcode}
                            onChange={(value) => setZipcode(value)}
                            placeholder=""
                            allowClear
                            showSearch
                            options={
                                data?.getPickupAppointmentListing?.content
                                    ? Array.from(new Set(data.getPickupAppointmentListing.content.map(item => item.zipcode)))
                                        .map(code => ({label: code, value: code}))
                                    : []
                            }
                        />
                    </div>
                    <div className="flex items-center">
                        <span className="mr-2">{t('pickup.appointment.taskCreateDrawer.address') || ''}</span>
                        <Input
                            style={{width: 240}}
                            value={addressFilter}
                            onChange={(event) => setAddressFilter(event.target.value)}
                            placeholder={t('pickup.appointment.taskCreateDrawer.address') || ''}
                        />
                    </div>
                    <div className="flex items-center">
                        <span className="mr-2">{t('pickup.appointment.taskCreateDrawer.phoneNumber') || ''}</span>
                        <Input
                            style={{width: 120}}
                            value={contactPhoneFilter}
                            onChange={(event) => setContactPhoneFilter(event.target.value)}
                            placeholder={t('pickup.appointment.taskCreateDrawer.phoneNumber') || ''}
                        />
                    </div>
                    <div className="flex items-center">
                        <span className="mr-2">{t('pickup.appointment.taskCreateDrawer.expectedUnit') || ''}</span>
                        <Select
                            style={{width: 120}}
                            options={unitOptions}
                            value={unitType}
                            onChange={(value) => setUnitType(value)}
                            placeholder={t('components.input.all') || ''}
                            allowClear
                        />
                    </div>

                </div>

                {/* 表格 */}
                <Table
                    rowSelection={{
                        type: 'checkbox',
                        selectedRowKeys,
                        onChange: handleSelectChange,
                        getCheckboxProps: (record) => ({
                            disabled: record.status !== 'CREATED' || disabledIds.includes(record.id)
                        })
                    }}
                    columns={columns}
                    dataSource={data?.getPickupAppointmentListing?.content || []}
                    rowKey="id"
                    loading={loading}
                    pagination={false}
                    size="small"
                    className="mb-4"
                    rowClassName={(record) => {
                        const reason = getDisabledReason(record);
                        if (reason) {
                            return 'text-gray-400 bg-gray-50';
                        }
                        return '';
                    }}
                />

                {/* 分页 */}
                <TablePaginationBar
                    total={totalElements}
                    current={currentPage}
                    pageSize={pageSize}
                    onChange={handlePageChange}
                    showQuickJumper={false}
                />


                {/* 底部按钮 */}
                <div className="flex justify-end mt-4 space-x-2">
                    <Button
                        onClick={onClose}
                        className="text-purple-600"
                    >
                        {t('components.button.cancel') || ''}
                    </Button>
                    <Button
                        type="primary"
                        onClick={handleAdd}
                        loading={isValidating}
                        disabled={isValidating}
                        className="bg-purple-600 hover:bg-purple-700"
                    >
                        {t('components.button.ok') || ''}
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default PickupAppointmentAddModal;